import type { SessionResponse } from '@shared/types/index.ts';

/**
 * The last session this browser was told about, kept so the app opens without
 * a network.
 *
 * The sign-in cookie already survives a reload — it is `HttpOnly` and the
 * browser keeps it for weeks. What did not survive was the app's knowledge of
 * *who* that cookie belongs to, which only ever arrived from
 * `GET /auth/session`. So a cleaner who reloaded in a basement was signed in
 * by every measure the server would apply, and thrown out to the login screen
 * by the only measure the client had — with a walkthrough in progress and a
 * customer standing beside them.
 *
 * This is that answer, written down. It is a cache and never a source of
 * truth: the real session query still runs on every load, overwrites this the
 * moment it lands, and any 401 wipes it. All it decides is what the app
 * believes in the seconds — or the hours, in a basement — before the server
 * gets a chance to say.
 *
 * Local storage rather than IndexedDB because it has to be readable
 * synchronously, during the first render, before anything can await.
 */

const KEY = 'cqb:session';

/**
 * Bumped when `SessionResponse` changes shape in a way an old copy would
 * misrepresent. A version nobody recognizes is discarded rather than guessed
 * at — the cost is one trip to the login screen after a deploy, and the
 * alternative is a header rendered from a shape that no longer exists.
 */
const VERSION = 1;

/**
 * The CSRF token is deliberately not kept.
 *
 * Nothing in the browser client reads it — requests take the token from the
 * readable `csrf` cookie, which the browser is already storing properly — so
 * persisting a second copy would be a credential written to disk for no
 * reader at all. Restored sessions carry an empty string, which is exactly as
 * true as it sounds: it will be replaced by the live session response.
 */
type CachedSession = Omit<SessionResponse, 'csrfToken'>;

/**
 * How long a remembered identity is allowed to stand on its own.
 *
 * The cookie it stands for lasts thirty days (see the auth route), and this is
 * shorter on purpose: the cache is only ever a stand-in for a request that
 * could not be made, and with no request there is nothing that can ever return
 * the 401 that would clear it. A device left in a van for a fortnight and then
 * opened with no signal should not present the last user's company, their
 * customers' names and their device-local walkthroughs to whoever has it now.
 * A week covers every offline stretch anybody actually works through; past
 * that, the app asks somebody to sign in.
 */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

interface Envelope {
  v: number;
  /** When the server last said this, in epoch milliseconds. */
  savedAt: number;
  session: CachedSession;
}

export function readCachedSession(): SessionResponse | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Envelope;
    if (parsed?.v !== VERSION || !parsed.session?.user || !parsed.session?.company) {
      window.localStorage.removeItem(KEY);
      return null;
    }
    // Older than the grace period, or written by a clock that has since moved
    // backwards — either way it is no longer something to be believed.
    const age = Date.now() - (parsed.savedAt ?? 0);
    if (!Number.isFinite(age) || age < 0 || age > MAX_AGE_MS) {
      window.localStorage.removeItem(KEY);
      return null;
    }
    // A public-demo session written by an older build of this file. Not
    // believed, and not kept — see `cacheSession`.
    if (parsed.session.publicDemo) {
      window.localStorage.removeItem(KEY);
      return null;
    }
    return { ...parsed.session, csrfToken: '' };
  } catch {
    // Unreadable, unparseable, or a browser that refuses local storage
    // altogether. Every one of those is "we do not know who this is", which
    // is the state the app was in before this file existed.
    return null;
  }
}

/** Remembers a session, or forgets the remembered one when handed null. */
export function cacheSession(session: SessionResponse | null): void {
  try {
    if (!session) {
      window.localStorage.removeItem(KEY);
      return;
    }
    // An operator signed in as somebody else is not an identity this device
    // may keep. The Worker gives impersonation sixty minutes on purpose;
    // remembering it here would hand that operator up to a week of the
    // tenant's application — and of the walkthroughs, drafts and customer
    // names sitting in this browser — the moment they opened it with no
    // signal to correct them.
    if (session.impersonatedBy) {
      window.localStorage.removeItem(KEY);
      return;
    }
    // Nor is a public-demo visitor, for the same reason and more sharply. That
    // session lasts two hours on the server; remembering it here would let
    // somebody who reloads offline after it has expired go on browsing the
    // shared company — everybody else's sample work, and whatever the visitors
    // before them typed — for up to a week, with the offline tenant cache
    // filling in behind it. There is nothing to keep an anonymous visitor
    // signed in FOR: the demo is two hours at a desk with a connection, not a
    // van in a basement, and the door they came through is still open.
    if (session.publicDemo) {
      window.localStorage.removeItem(KEY);
      return;
    }
    const { csrfToken: _csrfToken, ...rest } = session;
    const envelope: Envelope = { v: VERSION, savedAt: Date.now(), session: rest };
    window.localStorage.setItem(KEY, JSON.stringify(envelope));
  } catch {
    // A full or disabled store. The app works, it just opens online-only —
    // which is not worth failing a sign-in over.
  }
}
